package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.ApplicantStatus
import com.evoapps.evoshift.domain.model.ApplicationFilter
import com.evoapps.evoshift.domain.model.CandidateMail
import com.evoapps.evoshift.domain.model.CvFileType
import com.evoapps.evoshift.domain.model.CvSettings
import com.evoapps.evoshift.domain.model.Interview
import com.evoapps.evoshift.domain.model.InterviewRequest
import com.evoapps.evoshift.domain.model.InvalidInterviewException
import com.evoapps.evoshift.domain.model.RejectionMailTemplate
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.isActorFor
import com.evoapps.evoshift.domain.model.isReaderFor
import com.evoapps.evoshift.domain.repository.InterviewRepository
import com.evoapps.evoshift.domain.usecase.hiring.CancelInterviewUseCase
import com.evoapps.evoshift.domain.usecase.hiring.RejectApplicantUseCase
import com.evoapps.evoshift.domain.usecase.hiring.ScheduleInterviewUseCase
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import evoshift.composeapp.generated.resources.applicant_marked_unread
import evoshift.composeapp.generated.resources.applicant_reject_already_sent
import evoshift.composeapp.generated.resources.applicant_rejected
import evoshift.composeapp.generated.resources.applicant_rejected_mail
import evoshift.composeapp.generated.resources.applicant_rejected_no_email
import evoshift.composeapp.generated.resources.interview_cancelled
import evoshift.composeapp.generated.resources.interview_clash
import evoshift.composeapp.generated.resources.interview_moved
import evoshift.composeapp.generated.resources.interview_moved_quiet
import evoshift.composeapp.generated.resources.interview_scheduled
import evoshift.composeapp.generated.resources.interview_scheduled_quiet
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.stateIn
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.InvalidJobPostException
import com.evoapps.evoshift.domain.model.JobApplication
import com.evoapps.evoshift.domain.model.JobContractType
import com.evoapps.evoshift.domain.model.JobPost
import com.evoapps.evoshift.domain.model.JobPostStatus
import com.evoapps.evoshift.domain.model.PayUnit
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.HiringRepository
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.hiring.SaveJobPostUseCase
import com.evoapps.evoshift.domain.usecase.hiring.SetApplicantStatusUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.applicant_deleted
import evoshift.composeapp.generated.resources.applicant_saved
import evoshift.composeapp.generated.resources.job_closed
import evoshift.composeapp.generated.resources.job_deleted
import evoshift.composeapp.generated.resources.job_published
import evoshift.composeapp.generated.resources.job_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

// --- Team → Hiring: the posts ------------------------------------------------------------------

data class HiringUiState(
    val isLoading: Boolean = true,
    val workplaceId: String? = null,
    val viewerId: String = "",
    val capabilities: Set<Capability> = emptySet(),
    val posts: List<JobPost> = emptyList(),
    /** SH-210: the viewer's own unread applications per post (their read state). */
    val unreadByPost: Map<String, Int> = emptyMap(),
    val message: UiText? = null
) {
    val canEdit: Boolean get() = capabilities.shows(Gate.HIRING_EDITING)
    /** Managers see every post; anyone else only the posts they recruit for (SH-210). */
    val isRecruiterOnly: Boolean get() = !capabilities.shows(Gate.HIRING)

    /** The viewer's unread applications of [post] (the server's NEW count until they are counted). */
    fun unreadOf(post: JobPost): Int = unreadByPost[post.id] ?: post.newApplicants
}

/**
 * SH-125: the workplace's job posts (Team → Hiring) with their applicant counts, for its managers
 * ([Gate.HIRING]). Follows the selected workplace. SH-210: anyone else sees the posts they recruit
 * for (read only), and each post shows the viewer's own unread count, worked out from the
 * applications' read state (readBy): a per-viewer count the server's `counts.new` (the NEW stage,
 * the same for everyone) cannot give.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class HiringViewModel(
    private val repository: HiringRepository,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observePlanActive: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(HiringUiState())
    val uiState: StateFlow<HiringUiState> = _uiState.asStateFlow()
    private val postsJob = JobSlot()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                val user = getCurrentUser().first() ?: return@collectLatest
                if (workplaceId == null) {
                    _uiState.update { it.copy(isLoading = false, workplaceId = null, posts = emptyList()) }
                    return@collectLatest
                }
                val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                _uiState.update { state ->
                    state.copy(
                        workplaceId = workplaceId, viewerId = user.uid, capabilities = capabilities,
                        posts = if (state.workplaceId == workplaceId) state.posts else emptyList(),
                        isLoading = state.workplaceId != workplaceId || state.posts.isEmpty()
                    )
                }
                val manager = capabilities.shows(Gate.HIRING)
                if (!manager && Capability.WORK_SHIFTS !in capabilities) {
                    _uiState.update { it.copy(isLoading = false, posts = emptyList()) }
                    return@collectLatest
                }
                postsJob.launch(viewModelScope) {
                    val posts = repository.observePosts(workplaceId, recruiterId = if (manager) null else user.uid)
                        .catch { e -> _uiState.update { it.copy(isLoading = false, message = e.toUiText()) } }
                    posts.onEach { list -> _uiState.update { it.copy(isLoading = false, posts = list) } }
                        .flatMapLatest { list -> unreadCounts(list, user.uid) }
                        .collect { unread -> _uiState.update { it.copy(unreadByPost = unread) } }
                }
            }
        }
    }

    /** Each post's applications the viewer has not opened yet. */
    private fun unreadCounts(posts: List<JobPost>, viewerId: String): Flow<Map<String, Int>> {
        if (posts.isEmpty()) return flowOf(emptyMap())
        return combine(posts.map { post ->
            repository.observeApplications(post.id)
                .map { apps -> post.id to apps.count { !it.isReadBy(viewerId) } }
                .catch { emit(post.id to post.newApplicants) }
        }) { pairs -> pairs.toMap() }.distinctUntilChanged()
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}

/**
 * SH-210: whether the signed-in person recruits for a job post of the selected workplace (the More
 * hub's Hiring row for recruiters who are not managers). False on any error.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class RecruitingViewModel(
    repository: HiringRepository,
    getCurrentUser: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager
) : ViewModel() {
    val recruiting: StateFlow<Boolean> = combine(getCurrentUser(), sessionManager.currentWorkplaceId) { user, wp -> user to wp }
        .flatMapLatest { (user, wp) ->
            if (user == null || wp == null) flowOf(false)
            else repository.observePosts(wp, recruiterId = user.uid).map { it.isNotEmpty() }.catch { emit(false) }
        }
        .distinctUntilChanged()
        .stateIn(viewModelScope, kotlinx.coroutines.flow.SharingStarted.WhileSubscribed(5_000), false)
}

// --- A job post's editor -------------------------------------------------------------------------

/** The editor's fields as typed. */
data class JobPostForm(
    val title: String = "",
    val role: String = "",
    val contractType: JobContractType = JobContractType.EMPLOYMENT,
    val payMin: String = "",
    val payMax: String = "",
    val payUnit: PayUnit = PayUnit.HOUR,
    val hoursText: String = "",
    val city: String = "",
    val closesOn: String = "",
    val description: String = "",
    val employerName: String = "",
    val privacyContact: String = "",
    val lang: String = "pl",
    /** SH-210: the CV the form asks for, the recruiters, the rejection e-mail. */
    val cv: CvSettings = CvSettings(),
    val recruiterIds: List<String> = emptyList(),
    val rejectionMail: RejectionMailTemplate = RejectionMailTemplate()
)

data class JobPostEditorUiState(
    val isLoading: Boolean = true,
    val isSaving: Boolean = false,
    val workplaceId: String? = null,
    val capabilities: Set<Capability> = emptySet(),
    val form: JobPostForm = JobPostForm(),
    /** The post as saved (null: a new one not saved yet): its id gives the share link. */
    val post: JobPost? = null,
    val roles: List<String> = emptyList(),
    /** SH-210: the team, to choose the recruiters from. */
    val team: List<User> = emptyList(),
    val currency: String = "PLN",
    /** What to fix, shown at its field after a save was refused. */
    val problem: InvalidJobPostException.Problem? = null,
    val message: UiText? = null,
    /** The post was deleted: the screen goes back. */
    val deleted: Boolean = false
) {
    val canEdit: Boolean get() = capabilities.shows(Gate.HIRING_EDITING)
}

/** "30,50" or "30.5" as a number; null when it is not one. */
internal fun parseAmount(text: String): Double? = text.trim().replace(" ", "").replace(',', '.').toDoubleOrNull()

/** An amount as the field shows it again: "30", "32.5". */
private fun Double.asField(): String = if (this <= 0.0) "" else if (this % 1.0 == 0.0) toLong().toString() else toString()

/**
 * SH-125: writes a job post (new when [postId] is null): the role, contract, pay range (required),
 * hours, place, last day, description, the employer and contact the page's privacy notice names,
 * and the page's language; saves it as a draft or publishes it, closes, reopens and deletes it.
 * Once saved, the screen shows its link and QR code.
 */
class JobPostEditorViewModel(
    private val postId: String?,
    private val repository: HiringRepository,
    private val savePost: SaveJobPostUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val getWorkplace: GetWorkplaceUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-210: the team the recruiters are chosen from. */
    private val getTeam: GetAssignableEmployeesUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(JobPostEditorUiState())
    val uiState: StateFlow<JobPostEditorUiState> = _uiState.asStateFlow()
    private var userId = ""

    init {
        viewModelScope.launch { load() }
    }

    private suspend fun load() {
        val user = getCurrentUser().first() ?: return
        userId = user.uid
        val existing = postId?.let { id ->
            repository.getPost(id).getOrElse { e ->
                _uiState.update { it.copy(isLoading = false, message = e.toUiText()) }
                return
            }
        }
        val workplaceId = existing?.workplaceId ?: sessionManager.currentWorkplaceId.first() ?: run {
            _uiState.update { it.copy(isLoading = false) }
            return
        }
        val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
        val workplace = getWorkplace(workplaceId).getOrNull()
        val form = existing?.toForm() ?: JobPostForm(
            employerName = workplace?.name.orEmpty(),
            city = "",
            lang = appLanguage()
        )
        val team = getTeam?.invoke(workplaceId)?.getOrNull().orEmpty()
            .filter { it.engagements[workplaceId]?.isActive != false }
            .sortedBy { it.displayName.lowercase() }
        _uiState.update {
            it.copy(
                isLoading = false, workplaceId = workplaceId, capabilities = capabilities, post = existing, form = form,
                roles = workplace?.settings?.availableRoles.orEmpty(), team = team,
                currency = existing?.currency ?: workplace?.settings?.currency ?: "PLN"
            )
        }
    }

    private fun JobPost.toForm() = JobPostForm(
        title = title, role = role, contractType = contractType, payMin = payMin.asField(), payMax = payMax.asField(),
        payUnit = payUnit, hoursText = hoursText, city = city, closesOn = closesOn.orEmpty(), description = description,
        employerName = employerName, privacyContact = privacyContact, lang = lang,
        cv = cv, recruiterIds = recruiterIds, rejectionMail = rejectionMail
    )

    /** SH-210: a person of the team in or out of the recruiters. */
    fun toggleRecruiter(userId: String) = edit { form ->
        form.copy(recruiterIds = if (userId in form.recruiterIds) form.recruiterIds - userId else form.recruiterIds + userId)
    }

    /** SH-210: a file kind in or out of what the post accepts. */
    fun toggleCvType(type: CvFileType) = edit { form ->
        form.copy(cv = form.cv.copy(types = if (type in form.cv.types) form.cv.types - type else form.cv.types + type))
    }

    /** A field changed (the form holds what is typed; it is checked on saving). */
    fun edit(change: (JobPostForm) -> JobPostForm) = _uiState.update { it.copy(form = change(it.form), problem = null) }

    /** Saves with [status]: DRAFT keeps it private, OPEN publishes it, CLOSED takes it down. */
    fun save(status: JobPostStatus) {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (state.isSaving || !state.canEdit) return
        val form = state.form
        val base = state.post ?: JobPost(id = "", workplaceId = workplaceId)
        val post = base.copy(
            status = status, title = form.title, role = form.role, contractType = form.contractType,
            payMin = parseAmount(form.payMin) ?: 0.0, payMax = parseAmount(form.payMax) ?: 0.0, payUnit = form.payUnit,
            currency = state.currency, hoursText = form.hoursText, city = form.city,
            closesOn = form.closesOn.ifBlank { null }, description = form.description, employerName = form.employerName,
            privacyContact = form.privacyContact, lang = form.lang,
            cv = form.cv, recruiterIds = form.recruiterIds, rejectionMail = form.rejectionMail
        )
        _uiState.update { it.copy(isSaving = true, problem = null) }
        viewModelScope.launch {
            savePost(post, userId).fold(
                onSuccess = { saved ->
                    val message = when {
                        status == JobPostStatus.OPEN -> Res.string.job_published
                        status == JobPostStatus.CLOSED && base.status != JobPostStatus.CLOSED -> Res.string.job_closed
                        else -> Res.string.job_saved
                    }
                    _uiState.update { it.copy(isSaving = false, post = saved, message = UiText.Resource(message)) }
                },
                onFailure = { e ->
                    _uiState.update {
                        // A field to fix says so under the field (no snackbar over the buttons).
                        val problem = (e as? InvalidJobPostException)?.problem
                        it.copy(isSaving = false, problem = problem, message = if (problem == null) e.toUiText() else null)
                    }
                }
            )
        }
    }

    /** Deletes the post with its applicants (the server deletes their CVs). */
    fun delete() {
        val state = _uiState.value
        val id = state.post?.id ?: return
        if (state.isSaving || !state.capabilities.shows(Gate.HIRING)) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            repository.deletePost(id).fold(
                onSuccess = { _uiState.update { it.copy(isSaving = false, deleted = true, message = UiText.Resource(Res.string.job_deleted)) } },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, message = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}

// --- The applicants of a post ----------------------------------------------------------------

/** SH-210: the interview dialog's outcome: open for [application] (a move of [interview]), or closed. */
data class InterviewDraft(val applicationId: String, val interview: Interview? = null)

data class ApplicantsUiState(
    val isLoading: Boolean = true,
    val post: JobPost? = null,
    val applications: List<JobApplication> = emptyList(),
    /** SH-210: All, Unread, Favourites, Rejected (for the viewer). */
    val filter: ApplicationFilter = ApplicationFilter.ALL,
    val viewerId: String = "",
    val capabilities: Set<Capability> = emptySet(),
    /** SH-210: the team (interview participants, names). */
    val team: List<User> = emptyList(),
    /** SH-210: the application open in the detail (its interviews are followed). */
    val openId: String? = null,
    val interviews: List<Interview> = emptyList(),
    /** SH-210: the interview dialog, when open; why its last save was refused. */
    val interviewDraft: InterviewDraft? = null,
    val interviewProblem: InvalidInterviewException.Problem? = null,
    val isSaving: Boolean = false,
    val message: UiText? = null,
    /** A CV link to open now (the screen opens it, then clears it). */
    val openUrl: String? = null
) {
    /** Reads the applications: the managers, and the post's recruiters (SH-210). */
    val canRead: Boolean get() = post?.isReaderFor(viewerId, capabilities) == true
    /** Acts on them (stage, note, rejecting, interviews) while the plan runs. */
    val canEdit: Boolean get() = post?.isActorFor(viewerId, capabilities) == true
    /** Deleting (GDPR) stays the managers', also once the plan ended. */
    val canDelete: Boolean get() = capabilities.shows(Gate.HIRING)
    val shown: List<JobApplication> get() = applications.filter { filter.matches(it, viewerId) }
    fun count(filter: ApplicationFilter): Int = applications.count { filter.matches(it, viewerId) }
    val open: JobApplication? get() = applications.firstOrNull { it.id == openId }
    fun nameOf(userId: String): String = team.firstOrNull { it.uid == userId }?.displayName ?: userId
}

/**
 * SH-125: a post's applicants for the workplace's managers: filter chips by stage, an applicant's
 * contact, availability, note, consents and CV, moving them to another stage with a note
 * (optimistic), deleting them, and "Hire" (the screen opens the employee form filled in).
 *
 * SH-210: the post's recruiters too. Filters All / Unread / Favourites / Rejected for the viewer;
 * opening an application marks it read for them ("Mark as unread" undoes it), the star marks a
 * favourite; rejecting with the post's e-mail (the server sends it once, never without an
 * address); interviews: scheduling, moving and cancelling, with the candidate's calendar
 * invitation, clashes said as a warning.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class ApplicantsViewModel(
    private val postId: String,
    private val repository: HiringRepository,
    private val setStatus: SetApplicantStatusUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val interviewRepository: InterviewRepository? = null,
    private val rejectApplicant: RejectApplicantUseCase? = null,
    private val scheduleInterview: ScheduleInterviewUseCase? = null,
    private val cancelInterview: CancelInterviewUseCase? = null,
    private val getTeam: GetAssignableEmployeesUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(ApplicantsUiState())
    val uiState: StateFlow<ApplicantsUiState> = _uiState.asStateFlow()
    private var userId = ""
    private val openFlow = MutableStateFlow<String?>(null)

    init {
        viewModelScope.launch {
            val user = getCurrentUser().first() ?: return@launch
            userId = user.uid
            val post = repository.getPost(postId).getOrElse { e ->
                _uiState.update { it.copy(isLoading = false, message = e.toUiText()) }
                return@launch
            }
            val capabilities = user.capabilitiesAt(post.workplaceId, observePlanActive.activeNow(post.workplaceId))
            _uiState.update { it.copy(post = post, capabilities = capabilities, viewerId = user.uid) }
            if (!post.isReaderFor(user.uid, capabilities)) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            launch {
                val team = getTeam?.invoke(post.workplaceId)?.getOrNull().orEmpty()
                    .filter { it.engagements[post.workplaceId]?.isActive != false }
                    .sortedBy { it.displayName.lowercase() }
                _uiState.update { it.copy(team = team) }
            }
            launch {
                val interviews = interviewRepository ?: return@launch
                openFlow.flatMapLatest { id ->
                    if (id == null) flowOf(emptyList()) else interviews.observeForApplication(postId, id).catch { emit(emptyList()) }
                }.collect { list -> _uiState.update { it.copy(interviews = list) } }
            }
            repository.observeApplications(postId)
                .catch { e -> _uiState.update { it.copy(isLoading = false, message = e.toUiText()) } }
                .collect { apps -> _uiState.update { it.copy(isLoading = false, applications = apps) } }
        }
    }

    fun filter(filter: ApplicationFilter) = _uiState.update { it.copy(filter = filter) }

    /** Opens [application]'s detail; it is read for the viewer from now on (shown at once). */
    fun open(application: JobApplication) {
        _uiState.update { it.copy(openId = application.id) }
        openFlow.value = application.id
        if (!application.isReadBy(userId)) setRead(application, read = true)
    }

    fun close() {
        _uiState.update { it.copy(openId = null, interviewDraft = null, interviewProblem = null) }
        openFlow.value = null
    }

    /** "Mark as unread": closes the detail, so it stays unread. */
    fun markUnread(application: JobApplication) {
        close()
        setRead(application, read = false, confirm = true)
    }

    private fun setRead(application: JobApplication, read: Boolean, confirm: Boolean = false) {
        if (!_uiState.value.canRead) return
        val before = _uiState.value.applications
        patch(application.id) { it.copy(readBy = if (read) it.readBy + userId else it.readBy - userId) }
        viewModelScope.launch {
            repository.setRead(postId, application.id, userId, read).fold(
                onSuccess = { if (confirm) _uiState.update { it.copy(message = UiText.Resource(Res.string.applicant_marked_unread)) } },
                onFailure = { e -> _uiState.update { it.copy(applications = before, message = e.toUiText()) } }
            )
        }
    }

    /** The viewer's star on [application], on or off (optimistic). */
    fun toggleStar(application: JobApplication) {
        if (!_uiState.value.canRead) return
        val before = _uiState.value.applications
        val starred = !application.isStarredBy(userId)
        patch(application.id) { it.copy(starredBy = if (starred) it.starredBy + userId else it.starredBy - userId) }
        viewModelScope.launch {
            repository.setStarred(postId, application.id, userId, starred).onFailure { e ->
                _uiState.update { it.copy(applications = before, message = e.toUiText()) }
            }
        }
    }

    private fun patch(applicationId: String, change: (JobApplication) -> JobApplication) = _uiState.update { state ->
        state.copy(applications = state.applications.map { if (it.id == applicationId) change(it) else it })
    }

    /** Moves [application] to [status] with [note]; shown at once, put back when the save fails. */
    fun update(application: JobApplication, status: ApplicantStatus, note: String?) {
        val before = _uiState.value
        if (!before.canEdit) return
        val cleanNote = note?.trim()?.takeIf { it.isNotEmpty() }
        patch(application.id) { it.copy(status = status, managerNote = cleanNote) }
        viewModelScope.launch {
            setStatus(application, status, note, userId).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.applicant_saved)) } },
                onFailure = { e -> _uiState.update { it.copy(applications = before.applications, message = e.toUiText()) } }
            )
        }
    }

    /**
     * SH-210: rejects [application] with [note]; with [sendMail], the post's rejection e-mail
     * (sent once by the server, only to an applicant with an e-mail address). Optimistic.
     */
    fun reject(application: JobApplication, sendMail: Boolean, note: String?) {
        val before = _uiState.value
        val use = rejectApplicant ?: return
        if (!before.canEdit) return
        patch(application.id) { it.copy(status = ApplicantStatus.REJECTED, managerNote = note?.trim()?.ifEmpty { null } ?: it.managerNote) }
        close()
        viewModelScope.launch {
            use(application, sendMail, note).fold(
                onSuccess = { mail ->
                    val text = when (mail) {
                        CandidateMail.QUEUED -> Res.string.applicant_rejected_mail
                        CandidateMail.NO_EMAIL -> Res.string.applicant_rejected_no_email
                        CandidateMail.ALREADY_SENT -> Res.string.applicant_reject_already_sent
                        CandidateMail.NOT_REQUESTED -> Res.string.applicant_rejected
                    }
                    _uiState.update { it.copy(message = UiText.Resource(text)) }
                },
                onFailure = { e -> _uiState.update { it.copy(applications = before.applications, message = e.toUiText()) } }
            )
        }
    }

    /** SH-210: opens the interview dialog for [application] (moving [interview] when given). */
    fun startInterview(application: JobApplication, interview: Interview? = null) {
        if (!_uiState.value.canEdit) return
        _uiState.update { it.copy(interviewDraft = InterviewDraft(application.id, interview), interviewProblem = null) }
    }

    fun dismissInterview() = _uiState.update { it.copy(interviewDraft = null, interviewProblem = null) }

    /** SH-210: schedules (or moves) the interview of the dialog; warns about clashes. */
    fun saveInterview(request: InterviewRequest) {
        val state = _uiState.value
        val use = scheduleInterview ?: return
        if (!state.canEdit || state.isSaving) return
        _uiState.update { it.copy(isSaving = true, interviewProblem = null) }
        viewModelScope.launch {
            use(request.copy(postId = postId)).fold(
                onSuccess = { result ->
                    val moved = request.interviewId != null
                    val sent = result.mail == CandidateMail.QUEUED
                    val clash = result.clashes.firstOrNull()
                    val message = when {
                        clash != null -> UiText.Resource(Res.string.interview_clash, listOf(_uiState.value.nameOf(clash.userId)))
                        moved && sent -> UiText.Resource(Res.string.interview_moved)
                        moved -> UiText.Resource(Res.string.interview_moved_quiet)
                        sent -> UiText.Resource(Res.string.interview_scheduled)
                        else -> UiText.Resource(Res.string.interview_scheduled_quiet)
                    }
                    _uiState.update { it.copy(isSaving = false, interviewDraft = null, message = message) }
                },
                onFailure = { e ->
                    val problem = (e as? InvalidInterviewException)?.problem
                    _uiState.update {
                        it.copy(isSaving = false, interviewProblem = problem, message = if (problem == null) e.toUiText() else null)
                    }
                }
            )
        }
    }

    /** SH-210: cancels [interview]; the candidate gets the calendar cancellation when [notify]. */
    fun cancel(interview: Interview, notify: Boolean = true) {
        val use = cancelInterview ?: return
        if (!_uiState.value.canEdit) return
        viewModelScope.launch {
            use(interview, notify).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.interview_cancelled)) } },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    /** Deletes [application] with its CV; optimistic. Allowed also once the plan ended (GDPR). */
    fun delete(application: JobApplication) {
        val before = _uiState.value
        if (!before.canDelete) return
        close()
        _uiState.update { state -> state.copy(applications = state.applications.filterNot { it.id == application.id }) }
        viewModelScope.launch {
            repository.deleteApplication(postId, application.id).fold(
                onSuccess = { _uiState.update { it.copy(message = UiText.Resource(Res.string.applicant_deleted)) } },
                onFailure = { e -> _uiState.update { it.copy(applications = before.applications, message = e.toUiText()) } }
            )
        }
    }

    /** Asks for a short-lived link to [application]'s CV; the screen opens it. */
    fun openCv(application: JobApplication) {
        if (application.cv == null || !_uiState.value.canRead) return
        viewModelScope.launch {
            repository.cvLink(postId, application.id).fold(
                onSuccess = { link -> _uiState.update { it.copy(openUrl = link.url) } },
                onFailure = { e -> _uiState.update { it.copy(message = e.toUiText()) } }
            )
        }
    }

    fun urlOpened() = _uiState.update { it.copy(openUrl = null) }

    fun messageShown() = _uiState.update { it.copy(message = null) }
}
